import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { CircularProgress } from '@mui/material';
import { sa } from '../superAdminUi';
import { getUserActivity } from '../../services/api/identityUserService';
import { formatAuditTime, readAuditPage } from '../superAdminUi/auditFields';
import { Timeline } from './userManagementStyle';

const Heading = styled.h6`
  margin: 0 0 14px;
  font-family: ${sa.font.family};
  font-weight: ${sa.font.bold};
  font-size: 14px;
  color: ${sa.color.text};
`;
const StateText = styled.p`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-family: ${sa.font.family};
  font-size: 13px;
  color: ${(p) => (p.$error ? sa.color.pink : sa.color.textGray)};
`;

// The "Reveal History" panel in Access Management. Loads once per expansion; owns its own
// loading/empty/error so an activity fetch never disturbs the surrounding user list.
//
// The endpoint returns the shared audit timeline entry — the same shape the Audit Log page and a
// super admin's history render — so this panel reads it through the same module and displays the
// rendered summary rather than composing one of its own.
function ActivityHistory({ userId }) {
  const [state, setState] = useState({ status: 'loading', items: [], error: '' });

  useEffect(() => {
    let active = true;
    setState({ status: 'loading', items: [], error: '' });
    getUserActivity(userId)
      .then((body) => {
        if (!active) return;
        const { entries } = readAuditPage(body);
        setState({ status: 'loaded', items: entries, error: '' });
      })
      .catch((error) => {
        if (!active) return;
        setState({ status: 'error', items: [], error: error?.message || 'Could not load activity.' });
      });
    return () => {
      active = false;
    };
  }, [userId]);

  return (
    <div className='activity-history'>
      <Heading>Activity History</Heading>

      {state.status === 'loading' && (
        <StateText>
          <CircularProgress size={16} sx={{ color: sa.color.teal }} />
          Loading activity…
        </StateText>
      )}

      {state.status === 'error' && <StateText $error>{state.error}</StateText>}

      {state.status === 'loaded' && state.items.length === 0 && (
        <StateText>No activity recorded yet.</StateText>
      )}

      {state.status === 'loaded' && state.items.length > 0 && (
        <Timeline>
          {state.items.map((item) => (
            <li key={item.id}>
              <span className='event-title'>{item.actionLabel}</span>
              <span className='event-meta'>{item.summary}</span>
              <span className='event-meta'>{formatAuditTime(item.occurredAt)}</span>
            </li>
          ))}
        </Timeline>
      )}
    </div>
  );
}

ActivityHistory.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default ActivityHistory;
